import { useEffect, useMemo, useRef, useState } from "react";
import { amortise, fmtGBP } from "../lib/calc";
import { Calculator as CalcIcon } from "lucide-react";
import ComplianceStrip from "../components/ComplianceStrip";
import BrokerCTA from "../components/BrokerCTA";
import ConveyancingCTA from "../components/ConveyancingCTA";
import SurveyQuoteCTA from "../components/SurveyQuoteCTA";
import { CashbackPromptCard } from "../components/CashbackPrompt";
import { cashbackPromotionEnabled } from "../config/promotions";
import Microdisclaimer from "../components/Microdisclaimer";
import Seo from "../components/Seo";
import SeoProse from "../components/SeoProse";
import { Slider } from "../components/ui/slider";
import { track } from "../lib/api";
import { PAGES } from "../content/site";
import { useEditableNumber } from "../hooks/useEditableNumber";

const Field = ({ label, value, onChange, type = "number", step, testid, hint, readOnly = false, placeholder }) => {
  const { displayValue, inputProps } = useEditableNumber(value, onChange, { allowEmpty: true });
  if (type !== "number" || readOnly) {
    return (
      <label className="block">
        <span className="mp-label">{label}</span>
        <input
          type={type}
          step={step}
          placeholder={placeholder}
          className="mp-input"
          value={value}
          onChange={(e) => onChange(type === "number" ? +e.target.value : e.target.value)}
          readOnly={readOnly}
          data-testid={testid}
        />
        {hint && <span className="mp-help">{hint}</span>}
      </label>
    );
  }
  return (
    <label className="block">
      <span className="mp-label">{label}</span>
      <input
        type="number"
        step={step}
        placeholder={placeholder}
        className="mp-input"
        value={displayValue}
        data-testid={testid}
        {...inputProps}
      />
      {hint && <span className="mp-help">{hint}</span>}
    </label>
  );
};

const SliderField = ({ label, value, onChange, min, max, step, unit = "", precision = 0, testid, hint }) => {
  const { displayValue, inputProps } = useEditableNumber(value, onChange, { min, max, fallback: min });
  const sliderValue = useMemo(() => [Number(value) || min], [value, min]);
  return (
    <div className="block sm:col-span-2">
      <div className="flex items-center justify-between mb-2">
        <span className="mp-label !mb-0">{label}</span>
        <div className="inline-flex items-center gap-1 rounded-lg border border-[#DCE7E3] bg-white px-2 py-1">
          <input
            type="number"
            inputMode="decimal"
            min={min}
            max={max}
            step={step}
            value={displayValue}
            className="w-16 sm:w-20 bg-transparent text-right font-sora text-sm font-semibold text-[#0B5F58] tabular-nums focus:outline-none"
            data-testid={`${testid}-input`}
            {...inputProps}
          />
          {unit && <span className="text-xs font-medium text-[#475569] pr-1" data-testid={`${testid}-value`}>{unit}</span>}
        </div>
      </div>
      <div className="rounded-xl border border-[#DCE7E3] bg-white px-4 py-4">
        <Slider
          min={min}
          max={max}
          step={step}
          value={sliderValue}
          onValueChange={(v) => onChange(v[0])}
          data-testid={testid}
        />
        <div className="mt-2 flex justify-between text-[11px] text-[#475569] tabular-nums">
          <span>{precision > 0 ? `${Number(min).toFixed(precision)}${unit}` : `${min}${unit ? " " + unit : ""}`}</span>
          <span>{precision > 0 ? `${Number(max).toFixed(precision)}${unit}` : `${max}${unit ? " " + unit : ""}`}</span>
        </div>
      </div>
      {hint && <span className="mp-help">{hint}</span>}
    </div>
  );
};

const RateTermSliders = ({ rate, setRate, term, setTerm, suffix }) => (
  <>
    <SliderField
      label="Interest rate"
      value={rate}
      onChange={setRate}
      min={1}
      max={10}
      step={0.01}
      unit="%"
      precision={2}
      testid={`calc-rate-slider-${suffix}`}
      hint="Drag or type — explore different rates."
    />
    <SliderField
      label="Mortgage term"
      value={term}
      onChange={setTerm}
      min={5}
      max={40}
      step={1}
      unit="yrs"
      testid={`calc-term-slider-${suffix}`}
    />
  </>
);

const ResultsPanel = ({ withOver, overpayment, useDirectLoan, ltv, monthsSaved, interestSaved }) => (
  <aside className="lg:col-span-2 lg:sticky lg:top-24 self-start mp-card p-6 md:p-8" data-testid="calc-results">
    <div className="text-xs uppercase tracking-widest text-[#0F766E]">Results — estimates only</div>
    <div className="mt-3">
      <div className="text-sm text-[#475569]">Estimated monthly repayment</div>
      <div className="font-sora text-4xl font-bold text-[#0B5F58]" data-testid="calc-result-monthly">{fmtGBP(withOver.monthlyPayment)}</div>
      {overpayment > 0 && <div className="text-xs text-[#475569] mt-1">includes £{overpayment} monthly overpayment</div>}
      <Microdisclaimer />
    </div>
    <hr className="my-5 border-[#DCE7E3]" />
    <ul className="space-y-3 text-sm">
      <li className="flex items-center justify-between"><span className="text-[#475569]">Total interest over term</span><span className="font-semibold text-[#0B1220]" data-testid="calc-result-interest">{fmtGBP(withOver.totalInterest)}</span></li>
      <li className="flex items-center justify-between"><span className="text-[#475569]">Total amount repaid</span><span className="font-semibold text-[#0B1220]" data-testid="calc-result-total">{fmtGBP(withOver.totalRepaid)}</span></li>
      {!useDirectLoan && (
        <li className="flex items-center justify-between"><span className="text-[#475569]">Loan-to-value</span><span className="font-semibold text-[#0B1220]" data-testid="calc-result-ltv">{ltv.toFixed(1)}%</span></li>
      )}
    </ul>
    {overpayment > 0 && (
      <div className="mt-5 rounded-xl bg-[#F7FAF9] border border-[#DCE7E3] p-4">
        <div className="text-xs uppercase tracking-widest text-[#10B981]">With overpayment</div>
        <p className="text-sm text-[#0B1220] mt-1">You'd clear the mortgage in about <strong>{withOver.yearsToClear.toFixed(1)} years</strong> ({Math.round(monthsSaved)} months sooner), saving roughly <strong>{fmtGBP(interestSaved)}</strong> in interest.</p>
      </div>
    )}
    <p className="mt-5 text-xs text-[#475569]">Figures are estimates only and do not represent a mortgage offer, recommendation, or regulated advice.</p>
  </aside>
);

const Calculator = () => {
  const [price, setPrice] = useState("");
  const [deposit, setDeposit] = useState("");
  const [rate, setRate] = useState(4.75);
  const [term, setTerm] = useState(25);
  const [overpayment, setOverpayment] = useState("");
  const [useDirectLoan, setUseDirectLoan] = useState(false);
  const [loanDirect, setLoanDirect] = useState("");

  // Mount-only view event; `track` is a stable module import (not a reactive dep).
  useEffect(() => { track("calculator_view"); }, []);

  const priceNum = Number(price) || 0;
  const depositNum = Number(deposit) || 0;
  const overpaymentNum = Number(overpayment) || 0;
  const loanDirectNum = Number(loanDirect) || 0;
  const loan = useDirectLoan ? loanDirectNum : Math.max(priceNum - depositNum, 0);
  const ltv = !useDirectLoan && priceNum > 0 ? (loan / priceNum) * 100 : 0;

  const base = useMemo(() => amortise(loan, rate, term, 0), [loan, rate, term]);
  const withOver = useMemo(() => amortise(loan, rate, term, overpaymentNum), [loan, rate, term, overpaymentNum]);

  // One-shot usage/results events — no entered figures are sent, only that the
  // calculator was used and produced results. Gated on actual user input so the
  // events never fire on mount (StrictMode-safe).
  const usedTracked = useRef(false);
  const resultsTracked = useRef(false);
  const interacted =
    price !== "" || deposit !== "" || loanDirect !== "" || overpayment !== "" ||
    rate !== 4.75 || term !== 25 || useDirectLoan;
  useEffect(() => {
    if (!interacted) return;
    if (!usedTracked.current) {
      usedTracked.current = true;
      track("mortgage_calculator_used", { source: "calculator", cta_location: "calculator" });
    }
    if (!resultsTracked.current && loan > 0) {
      resultsTracked.current = true;
      track("calculator_results_viewed", { source: "calculator", cta_location: "calculator" });
    }
    // `track` is a stable module import.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interacted, loan]);
  const monthsSaved = Math.max(base.monthsToClear - withOver.monthsToClear, 0);
  const interestSaved = Math.max(base.totalInterest - withOver.totalInterest, 0);

  return (
    <div className="mp-container py-14">
      <Seo
        title="UK Mortgage Calculator with Overpayment"
        description="Free UK mortgage repayment calculator. Estimate monthly repayments, total interest, loan-to-value and the impact of regular overpayments. Figures are educational estimates only — not a mortgage offer."
        path="/calculator"
      />
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-[#0F766E] flex items-center gap-2"><CalcIcon className="w-3.5 h-3.5" /> Mortgage calculator</div>
        <h1 className="font-sora mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-[#0B1220]">UK Mortgage Calculator with Overpayments</h1>
        <p className="mt-4 text-[#475569] leading-relaxed">
          Enter your property price, deposit and rate to see indicative monthly repayments, total interest over the term and how a regular monthly overpayment could help you clear the mortgage sooner.
        </p>
      </div>

      <div className="mt-10 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 mp-card p-6 md:p-8">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-sora text-xl font-semibold">Your figures</h2>
            <label className="text-xs inline-flex items-center gap-2 text-[#475569]">
              <input type="checkbox" checked={useDirectLoan} onChange={(e) => setUseDirectLoan(e.target.checked)} data-testid="calc-direct-loan-toggle" />
              I know my mortgage amount
            </label>
          </div>
          {useDirectLoan ? (
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Mortgage amount" value={loanDirect} onChange={setLoanDirect} testid="calc-loan-direct" placeholder="e.g. 255,000" />
              <Field label="Monthly overpayment (£)" value={overpayment} onChange={setOverpayment} testid="calc-overpayment-a" hint="Optional. See how it changes your term." placeholder="0" />
              <RateTermSliders rate={rate} setRate={setRate} term={term} setTerm={setTerm} suffix="a" />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Property price (£)" value={price} onChange={setPrice} testid="calc-price" placeholder="e.g. 300,000" />
              <Field label="Deposit (£)" value={deposit} onChange={setDeposit} testid="calc-deposit" placeholder="e.g. 30,000" />
              <Field label="Mortgage amount (calculated)" value={loan || ""} onChange={() => {}} testid="calc-loan-calc" hint="Property price minus deposit." readOnly placeholder="—" />
              <Field label="Monthly overpayment (£)" value={overpayment} onChange={setOverpayment} testid="calc-overpayment-b" hint="Optional." placeholder="0" />
              <RateTermSliders rate={rate} setRate={setRate} term={term} setTerm={setTerm} suffix="b" />
            </div>
          )}
        </div>

        <ResultsPanel
          withOver={withOver}
          overpayment={overpayment}
          useDirectLoan={useDirectLoan}
          ltv={ltv}
          monthsSaved={monthsSaved}
          interestSaved={interestSaved}
        />
      </div>

      {/* CTAs — same layout pattern as the Stamp Duty page:
          section heading + one primary full-width CTA + two secondary cards.
          Broker is the strongest next step from the calculator so it's placed
          full-width at the top; conveyancing + survey sit below as secondary
          purchase-stage routes with matching source attribution. */}
      <section className="mt-10 max-w-4xl" onClickCapture={() => track("next_step_clicked", { cta_location: "post_calculator_next_step" })}>
        <h2 className="font-sora text-2xl sm:text-3xl font-semibold text-[#0B1220] tracking-tight">Ready for the next step?</h2>
        <BrokerCTA source="calculator_broker" variant="clickable-card" className="mt-6" />
        <div className="grid sm:grid-cols-2 gap-4 mt-4 items-start">
          <div className="h-full [&>a]:mt-0 [&>a]:h-full">
            <ConveyancingCTA source="calculator_conveyancing" variant="clickable-card" />
          </div>
          <div className="h-full [&>a]:mt-0 [&>a]:h-full">
            <SurveyQuoteCTA source="calculator_survey" variant="clickable-card" />
          </div>
        </div>
      </section>

      {cashbackPromotionEnabled && (
        <div className="mt-4 max-w-4xl">
          <CashbackPromptCard source="calculator" />
        </div>
      )}

      <SeoProse sections={PAGES["/calculator"].sections} className="mt-14" />

      <div className="mt-10"><ComplianceStrip /></div>
    </div>
  );
};

export default Calculator;
